// Users & roles (BUILD_SPEC.md §5.1, §6) — ADMIN only, both read and write
// (services/api/src/modules/config/{users,memberships}.ts): list tenant
// members, add a membership by email + role, change role, remove. A member can
// be invited by email before they've ever signed in (invite-by-email goal):
// `POST /config/memberships` mints a placeholder `users` row and the member
// shows as "Invited" (pending) in the table until they actually sign in with
// Microsoft, which claims it automatically.
import { useState } from 'react';
import { Alert, Badge, Button, Group, Modal, Paper, Select, Stack, Table, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { ROLES, type MembershipCreateInput, type Role } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useAuth } from '../../../auth/AuthContext';
import { ConfirmButton } from '../../../components/ConfirmButton';
import { useIsAdmin } from '../permissions';
import { useAddMembership, useMembers, useRemoveMembership, useUpdateMembershipRole } from '../api';
import type { MembershipRow } from '../types';

function AddMemberModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const add = useAddMembership();
  const form = useForm<MembershipCreateInput>({
    initialValues: { email: '', role: 'OPERATIONS' },
    validate: {
      email: (v) => (/\S+@\S+\.\S+/.test(v) ? null : 'A valid email is required'),
    },
  });

  function handleSubmit(values: MembershipCreateInput) {
    add.mutate(
      { email: values.email.trim(), role: values.role },
      {
        onSuccess: (created) => {
          notifications.show({
            color: created.invited ? 'amber' : 'brandGreen',
            title: created.invited ? 'Invite sent' : 'Member added',
            message: created.invited
              ? `${created.email} will get access the first time they sign in with Microsoft.`
              : `${created.email} was added to this tenant.`,
          });
          onClose();
          form.reset();
        },
      },
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Add tenant member" centered>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          {add.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {add.error instanceof ApiRequestError ? add.error.message : 'Failed to add member.'}
            </Alert>
          )}
          <TextInput
            label="Email"
            description="They can be invited by email before their first sign-in — access activates the moment they sign in with Microsoft"
            required
            {...form.getInputProps('email')}
          />
          <Select
            label="Role"
            description="Admin manages settings and users; Operations posts production, receipts and dispatches; Sales & Accounts also manages customers, invoices and Xero; Read-only can view but not post or edit."
            data={[...ROLES]}
            required
            {...form.getInputProps('role')}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={add.isPending}>
              Add member
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function MemberRow({ row }: { row: MembershipRow }) {
  const auth = useAuth();
  const updateRole = useUpdateMembershipRole(row.membershipId);
  const remove = useRemoveMembership(row.membershipId);
  const isSelf = auth.userEmail?.toLowerCase() === row.email.toLowerCase();

  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Text>{row.name}</Text>
          {row.pending && (
            <Tooltip label="Access activates the first time they sign in with Microsoft.">
              <Badge color="amber" variant="light">
                Invited
              </Badge>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
      <Table.Td>{row.email}</Table.Td>
      <Table.Td>
        <Tooltip label="Changes what this person can do — takes effect on their next action.">
          <Select
            data={[...ROLES]}
            value={row.role}
            onChange={(v) => v && updateRole.mutate({ role: v as Role })}
            w={180}
            disabled={updateRole.isPending}
          />
        </Tooltip>
      </Table.Td>
      <Table.Td>{new Date(row.createdAt).toLocaleDateString()}</Table.Td>
      <Table.Td>
        <Tooltip label="Removes this person from the tenant — they lose access immediately and must be re-added to come back." disabled={isSelf}>
          <span>
            <ConfirmButton
              variant="subtle"
              color="red"
              size="xs"
              leftSection={<IconTrash size={14} />}
              loading={remove.isPending}
              disabled={isSelf}
              title={isSelf ? "You can't remove your own membership" : undefined}
              label="Remove"
              message={`Remove ${row.name || row.email} from this tenant? They will immediately lose access and must be re-added to come back.`}
              onConfirm={() => remove.mutate()}
            />
          </span>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  );
}

export function UsersPanel() {
  const isAdmin = useIsAdmin();
  const { data, isLoading, isError, error } = useMembers();
  const [modalOpened, setModalOpened] = useState(false);
  const rows = data?.items ?? [];

  if (!isAdmin) {
    return (
      <Stack gap="md">
        <Title order={2}>Users &amp; roles</Title>
        <Alert color="gray" variant="light">
          ADMIN role is required to view or manage tenant members.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Users &amp; roles</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setModalOpened(true)}>
          Add member
        </Button>
      </Group>

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {error instanceof ApiRequestError ? error.message : 'Failed to load tenant members.'}
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={600}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Member since</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <MemberRow key={row.membershipId} row={row} />
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" ta="center" py="md">
                      No members yet.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <AddMemberModal opened={modalOpened} onClose={() => setModalOpened(false)} />
    </Stack>
  );
}
